import { useState, useEffect } from 'react';
import AdjustPositionDropdown from '../AdjustPositionDropdown';
import ProductGallery from '../ProductsAndStyle/ProductGallery';
import StyleGallery from '../ProductsAndStyle/StyleGallery';
import ProductDropdown from './ProductDropdown';
import PromptImageSelector from './PromptImageSelector';
import AspectRatioSelector from './AspectRatioSelector';
import PromptInput from './PromptInput';
import AdvancedSettings from './AdvancedSettings';
import ImageCounter from './ImageCounter';
import GenerateButton from './GenerateButton';
import StyleSelector from './StyleSelector';
import { uploadToS3 } from '../../../config/aws';
import { USER_NAME } from '../../../constant';
import { sendToBackend } from '../../../utils/utilities';
import { checkAvailableCredits, decreaseCredits } from '../../../dbApiFunctions';
import { showErrorAlert } from '../../../utils/sweetAlertConfig';
import '../../../styles/sweetAlert.css';
import { fal } from "@fal-ai/client";
import { GoogleGenAI } from '@google/genai';
import { useImage } from '../../../context/ImageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { extractKeyFromPresignedUrl } from '../../../config/aws';
import { repharsePrompt, describeImage } from '../../../services/chatgptService';
import InfoTooltip from '../../common/InfoTooltip';
import { allRefProducts } from '../../../refImagePrompts';

// Configure fal.ai client with credentials
fal.config({
  credentials: process.env.REACT_APP_FAL_AI_KEY
});

// Configure Gemini AI client
const ai = new GoogleGenAI({
  apiKey: process.env.REACT_APP_GEMINI_API_KEY,
});


const aspectRatioOptions = [
  { value: 1, label: '1:1', desc: 'Square' },
  { value: 16 / 9, label: '16:9', desc: 'Widescreen' },
  { value: 9 / 16, label: '9:16', desc: 'Social Story' },
  { value: 3 / 4, label: '3:4', desc: 'Traditional' },
  { value: 4 / 3, label: '4:3', desc: 'Classic' },
];

const CreateSidebar = ({ selectedProduct, setSelectedProduct, setCreateEstTime, setJobResponseCreate, setCreditsAvailable, setSelectedStyle, prompt, setPrompt, productPrompt, setProductPrompt, refImagePrompt, setRefImagePrompt, loadingCircle, setLoadingCircle, containerRef, imageRef, refNum, numberOfImages, setRefNum, setNumberOfImages, adjustDropdownIsOpen, setAdjustDropdownIsOpen, selectedReferenceImage, setSelectedReferenceImage, isRephraseClicked, setIsRephraseClicked, isRephrasing, setIsRephrasing, backdropOnly, setBackdropOnly, aspectRatio, setAspectRatio, reLight, setReLight, advancedSettingsOpen, setAdvancedSettingsOpen }) => {
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isStyleModalOpen, setIsStyleModalOpen] = useState(false);
  const [isPromptImageSelectorOpen, setIsPromptImageSelectorOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState("My Uploads");

  const { activeTab } = useImage();
  const s3_sub_folder = activeTab === 'bgh' ? "all_bghPage" : "all_createPage";

  // Check if at least 2 out of 3 options are selected
  const hasProduct = selectedProduct && selectedProduct.name !== "No Product Selected";
  const hasReferenceImage = selectedReferenceImage && selectedReferenceImage.name !== "Select Reference Image";
  const hasPrompt = prompt && prompt.trim().length > 0;
  const selectedCount = [hasProduct, hasReferenceImage, hasPrompt].filter(Boolean).length;
  const isGenerateDisabled = !hasPrompt && !(hasProduct && hasReferenceImage);

  useEffect(() => {
    document.body.style.overflow = (isProductModalOpen || isStyleModalOpen || isPromptImageSelectorOpen) ? 'hidden' : '';
  }, [isProductModalOpen, isStyleModalOpen, isPromptImageSelectorOpen]);

  const handleStyleSelect = (style) => {
    setSelectedStyle(style);
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
  };

  const getRefImagePrompt = async (referenceImage) => {
    // console.log("*******getRefImagePrompt =", referenceImage.src)
    if (referenceImage.src && referenceImage.name !== "Select Reference Image") {
      try {
        let base64Data;

        // Handle different types of image sources
        if (referenceImage.src.startsWith('data:')) {
          // Already base64
          base64Data = referenceImage.src;
        } else if (referenceImage.src.startsWith('blob:')) {
          // New Image Uploaded by user
          // Blob URL - convert to base64
          const response = await fetch(referenceImage.src);
          const blob = await response.blob();
          base64Data = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } else {
          if (selectedTab === "Our Collection") {
            const selectedProductName = referenceImage.name.split(".")[0];
            const selectedImageName = referenceImage.src.match(/filename%3D%22.*_(\d{3})\.png/)[1];
            const selectedPrompt = allRefProducts[selectedProductName][selectedImageName].prompt;
            const selectedAspectRatio = allRefProducts[selectedProductName][selectedImageName].aspectRatio;
            const aspectRatioJson = aspectRatioOptions.find(opt => opt.value === selectedAspectRatio);
            setRefImagePrompt(selectedPrompt);
            setAspectRatio(aspectRatioJson);
            // console.log("setting Ref Image prompt from excel=", selectedPrompt)
            return;
          }
          else {
            // image from user history in s3
            // External URL - try to fetch and convert
            try {
              // Try to fetch the image as a blob, handling CORS issues
              let blob;
              let fetchSuccess = false;
              try {
                const response = await fetch(referenceImage.src, {
                  mode: 'cors',
                  headers: {
                    'Accept': 'image/*'
                  }
                });
                if (response.ok) {
                  blob = await response.blob();
                  fetchSuccess = true;
                }
              } catch (corsError) {
                // fetch failed, will try canvas fallback
              }

              if (!fetchSuccess || !blob) {
                // Fallback: try to load image into canvas and get data
                try {
                  const img = new window.Image();
                  img.crossOrigin = 'Anonymous';
                  // Wait for image to load
                  await new Promise((resolve, reject) => {
                    img.onload = resolve;
                    img.onerror = reject;
                    // Add cache buster to avoid cached CORS errors
                    img.src = referenceImage.src + (referenceImage.src.includes('?') ? '&' : '?') + 't=' + Date.now();
                  });
                  // Draw to canvas
                  const canvas = document.createElement('canvas');
                  canvas.width = img.naturalWidth || img.width;
                  canvas.height = img.naturalHeight || img.height;
                  const ctx = canvas.getContext('2d');
                  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                  // Convert canvas to blob
                  blob = await new Promise((resolve, reject) => {
                    canvas.toBlob((b) => {
                      if (b) resolve(b);
                      else reject(new Error('Canvas toBlob failed'));
                    }, 'image/png');
                  });
                } catch (canvasError) {
                  console.warn('Could not fetch or draw external image for description:', canvasError);
                  return; // Exit gracefully if we can't fetch or draw the image
                }
              }

              base64Data = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
            } catch (fetchError) {
              console.warn('Could not fetch external image for description:', fetchError);
              return; // Exit gracefully if we can't fetch the image
            }
          }
        }

        // Call describeImage with the base64 data
        const description = await describeImage(base64Data);
        if (description) {
          setRefImagePrompt(description);
        }
      } catch (error) {
        console.error('Error processing reference image:', error);
        // Don't show error alert to user as this is just for logging
      }
    }
  }

  const handleReferenceImageSelect = async (referenceImage) => {
    setSelectedReferenceImage(referenceImage);
    await getRefImagePrompt(referenceImage);
  };

  const createFalImage = async (falModelName, imageUrl, timestamp, index, finalPrompt, referenceImageUrl = null) => {
    // console.log("createFalImage = Creating image with model ", falModelName);
    const result = await fal.subscribe(falModelName, {
      input: {
        prompt: finalPrompt + ", ultra-realistic, 8K, physically-based rendering, octane render, shot on Canon EOS R5 35 mm f/2.8 ISO 100, soft studio lighting with golden-hour fill, shallow depth-of-field bokeh, realistic wood grain & fabric texture, crisp focus, HDR, natural color grading, no CGI, no cartoon, no sketch",
        aspect_ratio: aspectRatio.label,
        guidance_scale: 8,
        safety_tolerance: 5,
        raw: true,
        image_url: imageUrl,
        ...(referenceImageUrl && { reference_image_url: referenceImageUrl })
      }
    });
    // console.log(result.data);
    const upscaledUrl = result.data.images[0].url;

    if (!upscaledUrl) {
      throw new Error("No image URL in response from upscaler");
    } else {
      // Fetch the image data from the URL
      const response = await fetch(upscaledUrl);
      const imageBlob = await response.blob();

      // Upload both low and high quality versions
      await uploadToS3(`USERS/${USER_NAME}/${s3_sub_folder}/${timestamp}/outputFolder/lowQuality/${index}.png`, 'image/png', imageBlob);
      await uploadToS3(`USERS/${USER_NAME}/${s3_sub_folder}/${timestamp}/outputFolder/highQuality/${index}.png`, 'image/png', imageBlob);
      // console.log("createFalImage = Uploaded low quality image to S3 at location ", `USERS/${USER_NAME}/${s3_sub_folder}/${timestamp}/outputFolder/lowQuality/${index}.png`);
      // console.log("createFalImage = Uploaded high quality image to S3 at location ", `USERS/${USER_NAME}/${s3_sub_folder}/${timestamp}/outputFolder/highQuality/${index}.png`);
    }
  }

  /**
   * Creates an image using Google's Gemini API
   * Works with any 2 inputs from [product_image, ref_image, prompt]
   * 
   * @param {string|null} imageUrl - Product image URL (optional)
   * @param {number} timestamp - Timestamp for file naming
   * @param {number} index - Image index for file naming
   * @param {string|null} finalPrompt - The prompt for image generation (optional)
   * @param {string|null} referenceImageUrl - Reference image URL (optional)
   * @param {string} model - Gemini model to use
   * 
   * Usage examples:
   * - Prompt only: await createGeminiImage(null, timestamp, i, "beautiful sunset", null);
   * - Product + Prompt: await createGeminiImage(productUrl, timestamp, i, "make it look professional", null);
   * - Product + Ref: await createGeminiImage(productUrl, timestamp, i, null, refUrl);
   * - Prompt + Ref: await createGeminiImage(null, timestamp, i, "create similar style", refUrl);
   */
  const createGeminiImage = async (imageUrl, timestamp, index, finalPrompt, referenceImageUrl = null, model = 'gemini-2.5-flash-image-preview') => {
    // console.log("createGeminiImage = Creating image with Gemini model", model);
    try {
      // Validate that we have at least 2 inputs
      const hasProduct = imageUrl && imageUrl.trim() !== '';
      const hasPrompt = finalPrompt && finalPrompt.trim() !== '';
      const hasReference = referenceImageUrl && referenceImageUrl.trim() !== '';
      
      const inputCount = [hasProduct, hasPrompt, hasReference].filter(Boolean).length;
      
      if (inputCount < 2) {
        throw new Error("At least 2 inputs required from [product_image, ref_image, prompt]");
      }

      // Create new AI instance for this request
      const ai = new GoogleGenAI({
        apiKey: process.env.REACT_APP_GEMINI_API_KEY,
      });

      const config = {
        responseModalities: ['IMAGE', 'TEXT'],
      };

      // Prepare the request parts
      const parts = [];
      
      // Add text prompt if provided
      if (hasPrompt) {
        const enhancedPrompt = finalPrompt + ", ultra-realistic, 8K, physically-based rendering, octane render, shot on Canon EOS R5 35 mm f/2.8 ISO 100, soft studio lighting with golden-hour fill, shallow depth-of-field bokeh, realistic wood grain & fabric texture, crisp focus, HDR, natural color grading, no CGI, no cartoon, no sketch";
        parts.push({
          text: `${enhancedPrompt}

Generate only the final high-quality output image. Do not include any watermarks, logos, text overlays, borders, frames, or additional visual elements. Output a single clean image with maximum resolution and quality. The image should be ${aspectRatio.label} aspect ratio.`
        });
      } else {
        // If no prompt, create a basic instruction
        parts.push({
          text: `Generate a high-quality image with ${aspectRatio.label} aspect ratio. Do not include any watermarks, logos, text overlays, borders, frames, or additional visual elements.`
        });
      }

      // Helper function to fetch image with CORS fallback
      const fetchImageWithCorsFallback = async (imageUrl) => {
        try {
          // Try direct fetch first
          const response = await fetch(imageUrl, {
            mode: 'cors',
            headers: {
              'Accept': 'image/*'
            }
          });
          if (response.ok) {
            const imageBlob = await response.blob();
            return imageBlob;
          }
        } catch (corsError) {
          console.warn('Direct fetch failed, trying canvas fallback:', corsError);
        }

        // Canvas fallback for CORS issues
        try {
          const img = new window.Image();
          img.crossOrigin = 'Anonymous';
          
          // Wait for image to load
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            // Add cache buster to avoid cached CORS errors
            img.src = imageUrl + (imageUrl.includes('?') ? '&' : '?') + 't=' + Date.now();
          });
          
          // Draw to canvas
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          // Convert canvas to blob
          return await new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
              if (blob) resolve(blob);
              else reject(new Error('Canvas toBlob failed'));
            }, 'image/png');
          });
        } catch (canvasError) {
          console.warn('Canvas fallback also failed:', canvasError);
          throw new Error('Could not fetch image with any method');
        }
      };

      // Add product image if provided
      if (hasProduct) {
        try {
          const imageBlob = await fetchImageWithCorsFallback(imageUrl);
          const base64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(imageBlob);
          });
          
          parts.push({
            inlineData: {
              mimeType: imageBlob.type || 'image/jpeg',
              data: base64.split(',')[1] // Remove data:image/...;base64, prefix
            }
          });
        } catch (fetchError) {
          console.warn('Could not fetch product image for Gemini:', fetchError);
          // Continue without product image
        }
      }

      // Add reference image if provided
      if (hasReference) {
        try {
          const imageBlob = await fetchImageWithCorsFallback(referenceImageUrl);
          const base64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(imageBlob);
          });
          
          parts.push({
            inlineData: {
              mimeType: imageBlob.type || 'image/jpeg',
              data: base64.split(',')[1] // Remove data:image/...;base64, prefix
            }
          });
        } catch (fetchError) {
          console.warn('Could not fetch reference image for Gemini:', fetchError);
          // Continue without reference image
        }
      }

      // Prepare the request in the correct format
      const contents = [
        {
          role: 'user',
          parts: parts,
        },
      ];

      // Generate the image using the correct API method
      // console.log('Sending request to Gemini API...');
      const response = await ai.models.generateContentStream({
        model,
        config,
        contents,
      });
      
      let fileIndex = 0;
      const results = [];
      let hasReceivedContent = false;

      // console.log('Processing response stream...');
      
      // Wait for the complete response stream
      for await (const chunk of response) {
        hasReceivedContent = true;
        
        if (!chunk.candidates || !chunk.candidates[0]?.content?.parts) {
          continue;
        }

        const parts = chunk.candidates[0].content.parts;
        
        for (const part of parts) {
          if (part.inlineData) {
            const inlineData = part.inlineData;
            
            try {
              // Convert base64 to blob using browser-compatible method
              const binaryString = atob(inlineData.data || '');
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              const blob = new Blob([bytes], { type: inlineData.mimeType || 'image/png' });
              
              // console.log(`Generated image: size: ${blob.size} bytes`);
              
              results.push({
                blob,
                mimeType: inlineData.mimeType || 'image/png'
              });
            } catch (err) {
              console.error('Error processing image data:', err);
            }
          } else if (part.text) {
            // console.log('Gemini text response:', part.text);
          }
        }
      }

      if (!hasReceivedContent) {
        throw new Error('No response received from Gemini API');
      }

      if (results.length === 0) {
        throw new Error('No image data received from Gemini API');
      }

      // Use the first generated image
      const imageBlob = results[0].blob;
      
      // Upload both low and high quality versions
      await uploadToS3(`USERS/${USER_NAME}/${s3_sub_folder}/${timestamp}/outputFolder/lowQuality/${index}.png`, 'image/png', imageBlob);
      await uploadToS3(`USERS/${USER_NAME}/${s3_sub_folder}/${timestamp}/outputFolder/highQuality/${index}.png`, 'image/png', imageBlob);
      // console.log("createGeminiImage = Uploaded low quality image to S3 at location ", `USERS/${USER_NAME}/${s3_sub_folder}/${timestamp}/outputFolder/lowQuality/${index}.png`);
      // console.log("createGeminiImage = Uploaded high quality image to S3 at location ", `USERS/${USER_NAME}/${s3_sub_folder}/${timestamp}/outputFolder/highQuality/${index}.png`);
      
    } catch (error) {
      console.error("Error in createGeminiImage:", error);
      throw new Error(`Failed to generate image with Gemini: ${error.message}`);
    }
  }

  const handleGenerate = async () => {
    // Validate that we have at least 2 inputs from [product_image, ref_image, prompt]
    const hasProduct = selectedProduct && selectedProduct.name !== "No Product Selected";
    const hasReferenceImage = selectedReferenceImage && selectedReferenceImage.name !== "Select Reference Image";
    const hasPrompt = prompt && prompt.trim().length > 0;

    const inputCount = [hasProduct, hasReferenceImage, hasPrompt].filter(Boolean).length;

    if (inputCount < 2) {
      showErrorAlert('Please provide at least 2 inputs from: prompt, product image, or reference image', 'Invalid Input');
      return;
    }

    setLoadingCircle(true);

    const timestamp = new Date().getTime();
    let formJson = {
      productName: selectedProduct.name,
      prompt: prompt,
      totalImages: numberOfImages,
      // style: selectedStyle.name === "Select Style" ? null : selectedStyle.name,
      // styleImageUrl: selectedStyle.name === "Select Style" ? null : selectedStyle.src,
      // creativeControl: options[value].label,
      // creativeControlValue: value,
      userName: USER_NAME,
      timestamp: timestamp,
      time: new Date().toLocaleString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        month: 'short',
        day: 'numeric',
        hour12: false
      }),
      refNum: refNum,
      // maxstyle: maxstyle,
      referenceImage: selectedReferenceImage.name !== "Select Reference Image" ? selectedReferenceImage.src : null,
      backdropOnly: backdropOnly,
      reLight: reLight,
      aspectRatio: aspectRatio.label,
      isRephraseClicked: isRephraseClicked
    };

    if (formJson.referenceImage) {
      if (!formJson.referenceImage.startsWith('blob:')) {
        formJson['referenceImage'] = extractKeyFromPresignedUrl(formJson.referenceImage);
      }
      else {
        try {
          // Convert base64 image to blob for upload
          const base64Response = await fetch(formJson.referenceImage);
          const blob = await base64Response.blob();
          await uploadToS3(`USERS/${USER_NAME}/all_refImages/${timestamp}.png`, blob.type, blob);
          // console.log("handleGenerate = Uploaded reference image to S3 at location ", `USERS/${USER_NAME}/all_refImages/${timestamp}.png`);
          formJson['referenceImage'] = `USERS/${USER_NAME}/all_refImages/${timestamp}.png`;
        } catch (error) {
          console.error('Error uploading accessory image:', error);
        }
      }
    }

    // AdjustPositionDropdown = Capture the current position and scale 
    if (imageRef.current) {
      const position = imageRef.current.style.transform;
      // Extract position values using regex
      const translateMatch = position.match(/translate\(([^,]+)px,\s*([^)]+)px\)/);
      const scaleMatch = position.match(/scale\(([^)]+)\)/);
      const dropdownContent = document.querySelector('#dropdown-content');
      formJson['x'] = translateMatch ? parseFloat(translateMatch[1]) : 0;
      formJson['y'] = translateMatch ? -1 * parseFloat(translateMatch[2]) : 0;
      formJson['scale'] = scaleMatch ? parseFloat(scaleMatch[1]) : 1;
      formJson['canvasWidth'] = dropdownContent.offsetWidth;
      formJson['canvasHeight'] = dropdownContent.offsetHeight;
      // formJson['randomize'] = randomize;
    }
    await uploadToS3(`USERS/${USER_NAME}/${s3_sub_folder}/${timestamp}/metadata.json`, 'application/json', JSON.stringify(formJson));
    // console.log("handleGenerate = Uploaded metadata to S3 at location ", `USERS/${USER_NAME}/${s3_sub_folder}/${timestamp}/metadata.json`);

    // console.log("Enhancing prompt INPUT  ")
    // console.log("*******productPrompt=", productPrompt)
    // console.log("*******isRephraseClicked=", isRephraseClicked)
    // console.log("*******refImagePrompt=", refImagePrompt)
    // console.log("*******prompt=", prompt)

    // enhanced Prompt
    let finalPrompt = prompt;
    if (!isRephraseClicked) {
      const combinedPrompt = (productPrompt || '') + (refImagePrompt || '') + (prompt || '');
      // console.log("*******combinedPrompt=", combinedPrompt);
      if (combinedPrompt.trim()) {
        finalPrompt = await repharsePrompt(combinedPrompt);
        // console.log("*******repharsePrompt returned=", finalPrompt);
      } else {
        // console.log("*******combinedPrompt is empty, using original prompt");
      }
    } else {
      // console.log("*******isRephraseClicked is true, using original prompt");
    }

    // Ensure we have a valid prompt
    if (!finalPrompt || !finalPrompt.trim()) {
      console.error("*******finalPrompt is empty or invalid:", finalPrompt);
      showErrorAlert('Please provide a prompt or select a product/reference image', 'Empty Prompt');
      setLoadingCircle(false);
      return;
    }

    formJson['prompt'] = finalPrompt;
    // console.log("*******enhancedPrompt=", formJson['prompt'])

    try {
      let response = {}
      const backendPromises = [];
      // Loop value times to simulate multiple image generation
      // setCreateEstTime(120);
      // for (let i = 0; i < numberOfImages-1; i++) {
      //   formJson['pid'] = i;
      //   response = sendToBackend('create', formJson, numberOfImages);
      // }
      const password = sessionStorage.getItem("password");
      const email = sessionStorage.getItem("email");
      const creditsAvailable = await checkAvailableCredits(email, password, numberOfImages);
      if (!creditsAvailable.status) {
        showErrorAlert(creditsAvailable.message, 'Insufficient Credits');
        return;
      }
      // console.log("*******all user input =", formJson)
      if (!formJson.reLight && !formJson.backdropOnly) {
        let falModelName = "fal-ai/flux-pro/v1.1-ultra";
        if (selectedProduct.name !== "No Product Selected") {
          falModelName = "fal-ai/flux-pro/kontext/max";
        }

        try {
          const geminiPromises = [];
          const productImageUrl = selectedProduct.name !== "No Product Selected" ? selectedProduct.src : null;
          const referenceImageUrl = selectedReferenceImage.name !== "Select Reference Image" ? selectedReferenceImage.src : null;
          const finalPrompt = prompt && prompt.trim().length > 0 ? prompt : null;
          
          // console.log("*******input to gemini product=", productImageUrl, "timestamp=", timestamp, "finalPrompt=", finalPrompt, "referenceImageUrl=", referenceImageUrl)
          for (let i = 0; i < numberOfImages; i++) {
            // geminiPromises.push(createFalImage(falModelName, selectedProduct.src, timestamp, i, finalPrompt, referenceImageUrl));
            geminiPromises.push(createGeminiImage(productImageUrl, timestamp, i, finalPrompt, referenceImageUrl));
          }
          await Promise.all(geminiPromises);
          await decreaseCredits(email, password, numberOfImages, "create");
        } catch (error) {
          console.error("Error generating images:", error);
          showErrorAlert("Failed to generate images. Please try again.");
          return;
        }
      }
      else {
        formJson['prompt'] = finalPrompt + ", ultra-realistic, 8K, physically-based rendering, octane render, shot on Canon EOS R5 35 mm f/2.8 ISO 100, soft studio lighting with golden-hour fill, shallow depth-of-field bokeh, realistic wood grain & fabric texture, crisp focus, HDR, natural color grading, no CGI, no cartoon, no sketch";
        if (numberOfImages > 1) {
          const metadata2 = structuredClone(formJson);
          metadata2['pid'] = numberOfImages - 2;
          backendPromises.push(sendToBackend('create', metadata2, 0, setCreditsAvailable));
        }

        if (numberOfImages > 2) {
          const metadata3 = structuredClone(formJson);
          metadata3['pid'] = numberOfImages - 3;
          backendPromises.push(sendToBackend('create', metadata3, 0, setCreditsAvailable));
        }

        if (numberOfImages > 3) {
          const metadata4 = structuredClone(formJson);
          metadata4['pid'] = numberOfImages - 4;
          backendPromises.push(sendToBackend('create', metadata4, 0, setCreditsAvailable));
        }

        formJson['pid'] = numberOfImages - 1;
        backendPromises.push(sendToBackend('create', formJson, numberOfImages, setCreditsAvailable));

        // Wait for all backend calls to complete
        const responses = await Promise.all(backendPromises);
        response = responses[responses.length - 1]; // Get the last response

        // console.log("Create response is=", response);
        setJobResponseCreate(response);
        // setCreateEstTime(response.est_time);
        if (response && response.hasOwnProperty('est_time')) {
          // console.log("Create waiting time is=", response.est_time);
        } else {
          // console.log("Create waiting time is not available");
        }
      }
    } catch (error) {
      console.error("Error in handleGenerate:", error);
      showErrorAlert("Failed to generate images. Please try again.");
    } finally {
      setCreateEstTime(3);
      setLoadingCircle(false);
    }
    setLoadingCircle(false);
    setIsRephraseClicked(false);
    setPrompt("");
  };

  return (
    <>
      <div className={`fixed inset-0 backdrop-blur-lg transition-all duration-500 ${(isProductModalOpen || isStyleModalOpen || isPromptImageSelectorOpen) ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}></div>
      <div className={`relative ${(isProductModalOpen || isStyleModalOpen || isPromptImageSelectorOpen) ? 'blur-lg' : ''}`}>
        <div className="fixed top-0 left-0 h-screen w-full lg:w-1/4 bg-white flex flex-col">
          <div className="p-4 lg:p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">Create and Edit</h2>
          </div>
          <div
            className="flex-1 overflow-y-auto p-4 lg:p-6"
            onMouseEnter={(e) => {
              document.body.style.overflow = 'hidden';
            }}
            onMouseLeave={(e) => {
              document.body.style.overflow = '';
            }}
          >
            <div className="flex flex-col pt-8">
              {/* Product Selector */}
              <div className="space-y-2 mb-4">
                <div className="text-sm text-black-800">Select Product</div>
                <ProductDropdown
                  selectedProduct={selectedProduct}
                  setIsProductModalOpen={setIsProductModalOpen}
                  setSelectedProduct={setSelectedProduct}
                  setProductPrompt={setProductPrompt}
                />
              </div>

              {/* Reference Image Selector */}
              <div className="space-y-2 mb-4">
                <div className="text-sm text-black-800">Select Reference Image (Optional)</div>
                <div
                  className="flex items-center justify-between p-1 border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer group relative overflow-hidden bg-white"
                  onClick={() => setIsPromptImageSelectorOpen(true)}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-[#4B3BFF] to-[#7B6BFF] opacity-0 group-hover:opacity-5 transition-all duration-300"></div>
                  <div className="flex items-center space-x-3 relative">
                    <img
                      alt={selectedReferenceImage.name}
                      className="w-10 h-10 rounded-lg group-hover:scale-110 transition-transform duration-300 object-cover"
                      height="40"
                      src={selectedReferenceImage.src}
                      width="40"
                    />
                    <span className="text-sm text-gray-800 group-hover:text-[#4B3BFF] transition-colors duration-300">
                      {selectedReferenceImage.name === "Select Reference Image" ? "Reference Image" : ""}
                    </span>
                  </div>
                  <div className="flex items-center relative z-10" >
                    {selectedReferenceImage.name !== "Select Reference Image" && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedReferenceImage({ "src": "https://img.icons8.com/?size=100&id=6RfZ-eZQz0ee&format=png&color=737373", "name": "Select Reference Image" });
                          setRefImagePrompt("");
                        }}
                        className="mr-1 text-gray-400 hover:text-red-500 transition-all duration-300 relative z-20"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                    <FontAwesomeIcon icon={faChevronRight} className="text-gray-400 group-hover:text-[#4B3BFF] group-hover:translate-x-1 transition-all duration-300 relative z-10 pr-2" />
                  </div>
                </div>
              </div>

              {/* Aspect Ratio Selector */}
              <div className="space-y-2 mb-4">
                <div className="text-sm text-black-800">Aspect Ratio</div>
                <AspectRatioSelector
                  aspectRatio={aspectRatio}
                  setAspectRatio={setAspectRatio}
                  aspectRatioOptions={aspectRatioOptions}
                />
              </div>

              {/* Prompt Input */}
              <div className="space-y-2 mb-0">
                <div className="text-sm text-black-800 flex items-center gap-1">
                  Prompt
                  <InfoTooltip iconSize={12}>
                    Always include the product category (e.g., bar chair, sofa). Focus on describing the scene, not the product features. The more detailed and vivid your scene description, the better the result.
                  </InfoTooltip>
                </div>
                <PromptInput
                  productPrompt={productPrompt}
                  refImagePrompt={refImagePrompt}
                  prompt={prompt}
                  setPrompt={setPrompt}
                  isRephrasing={isRephrasing}
                  setIsRephrasing={setIsRephrasing}
                  setIsRephraseClicked={setIsRephraseClicked}
                  promptPlaceholder="Describe your product image in natural language. 
Tip : Use the button to enhance prompt. If text box is blank, then button will generate a new prompt. Don't like it? Just delete and click again to generate a new one."
                  repharseAllowed={true}
                />
              </div>

              {/* Advanced Settings */}
              <div className="border border-gray-200 rounded-lg mt-2">
                <AdvancedSettings
                  advancedSettingsOpen={advancedSettingsOpen}
                  setAdvancedSettingsOpen={setAdvancedSettingsOpen}
                  backdropOnly={backdropOnly}
                  setBackdropOnly={setBackdropOnly}
                  reLight={reLight}
                  setReLight={setReLight}
                  containerRef={containerRef}
                  imageRef={imageRef}
                  mySelectedImage={selectedProduct}
                  adjustDropdownIsOpen={adjustDropdownIsOpen}
                  setAdjustDropdownIsOpen={setAdjustDropdownIsOpen}
                  aspectRatio={aspectRatio.value}
                />
              </div>
            </div>
          </div>

          <div className="bg-white p-2 lg:p-3 border-t border-gray-200 shadow-sm">
            <div className="flex items-center gap-3">
              <ImageCounter
                numberOfImages={numberOfImages}
                setNumberOfImages={setNumberOfImages}
              />

              <GenerateButton
                loadingCircle={loadingCircle}
                handleGenerate={handleGenerate}
                disabled={isGenerateDisabled}
              />
            </div>
          </div>
        </div>

        {isProductModalOpen && (
          <div className="fixed inset-0 z-50 flex justify-center items-center">
            <div className="absolute inset-0 bg-black bg-opacity-90 backdrop-blur-2xl transition-all duration-500">
            </div>
            <div className="relative z-10 transform transition-all duration-500 ease-out"
              style={{
                opacity: isProductModalOpen ? 1 : 0,
                transform: isProductModalOpen ? 'scale(1)' : 'scale(0.95)',
                height: '80vh'
              }}
            >
              <button
                onClick={() => setIsProductModalOpen(false)}
                className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center group hover:bg-[#4B3BFF] transition-all duration-300 transform hover:scale-110 hover:rotate-90"
              >
                <svg
                  className="w-4 h-4 text-gray-600 group-hover:text-gray-600 transition-colors duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <ProductGallery
                setIsProductModalOpen={setIsProductModalOpen}
                selectedSubProduct={selectedProduct}
                handleProductSelect={handleProductSelect}
                setRefNum={setRefNum}
                setCreditsAvailable={setCreditsAvailable}
                setProductPrompt={setProductPrompt}
              />
            </div>
          </div>
        )}

        {isStyleModalOpen && (
          <div className="fixed inset-0 z-50 flex justify-center items-center">
            <div
              className="absolute inset-0 bg-black bg-opacity-90 backdrop-blur-2xl transition-all duration-500"
            ></div>
            <div className="relative z-10 transform transition-all duration-500 ease-out"
              style={{
                opacity: isStyleModalOpen ? 1 : 0,
                transform: isStyleModalOpen ? 'scale(1)' : 'scale(0.95)',
                height: '80vh'
              }}
            >
              <button
                onClick={() => setIsStyleModalOpen(false)}
                className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center group hover:bg-[#4B3BFF] transition-all duration-300 transform hover:scale-110 hover:rotate-90"
              >
                <svg
                  className="w-4 h-4 text-gray-600 group-hover:text-gray-600 transition-colors duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <StyleGallery
                onSelectStyle={handleStyleSelect}
                onClose={() => setIsStyleModalOpen(false)}
                setCreditsAvailable={setCreditsAvailable}
              />
            </div>
          </div>
        )}

        {isPromptImageSelectorOpen && (
          <div className="fixed inset-0 z-50 flex justify-center items-center p-3">
            <div className="absolute inset-0 bg-black bg-opacity-90 backdrop-blur-2xl transition-all duration-500">
            </div>
            <div className="relative z-10 transform transition-all duration-500 ease-out max-h-[90vh] overflow-y-auto px-3"
              style={{
                opacity: isPromptImageSelectorOpen ? 1 : 0,
                transform: isPromptImageSelectorOpen ? 'scale(1)' : 'scale(0.95)',
              }}
            >
              <div className="relative">
                <PromptImageSelector
                  onSelectReferenceImage={handleReferenceImageSelect}
                  onClose={() => setIsPromptImageSelectorOpen(false)}
                  selectedTab={selectedTab}
                  setSelectedTab={setSelectedTab}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CreateSidebar; 